import { defineStore } from 'pinia'
import { ref, computed, shallowRef } from 'vue'
import type { User } from 'firebase/auth'
import {
  loginWithEmail,
  loginWithGoogle as authLoginWithGoogle,
  getPendingGoogleLink,
  linkGoogleToPasswordAccount,
  registerWithEmail as authRegisterWithEmail,
  sendEmailVerification,
  logout as firebaseLogout,
  type PendingGoogleLink,
} from '@/services/firebase/auth.service'
import { userService } from '@/services/userService'
import type { UserDocument } from '@/services/userService'
import { mapFirebaseAuthError } from '@/utils/authErrors'
import { useLegalConsentStore } from '@/stores/legalConsent'

export interface GoogleSignInOptions {
  /**
   * `true` sólo desde `/register`, con la casilla de los términos marcada: la
   * cuenta nueva se aprovisiona y su aceptación se registra (`signup_checkbox`).
   * Desde `/login` (por defecto) una cuenta sin `users/{uid}` NO se aprovisiona:
   * el modal «Un último paso» lo hace al aceptar (ADR-SEC-013 local).
   */
  acceptedTerms?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const userDocument = ref<UserDocument | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isRegistering = ref(false)
  // Credencial de Google pendiente de vincular (Op A.8). Sólo en memoria — nunca
  // se persiste (ADR-SEC-002 / ADR-SEC-012). `shallowRef`: es un objeto del SDK.
  const pendingGoogleLink = shallowRef<PendingGoogleLink | null>(null)
  const hasPendingGoogleLink = computed(() => pendingGoogleLink.value !== null)
  const pendingLinkEmail = computed(() => pendingGoogleLink.value?.email ?? null)

  const isAuthenticated = computed(() => user.value !== null)
  const userId = computed(() => user.value?.uid ?? null)
  const userEmail = computed(() => user.value?.email ?? null)
  const displayName = computed(() => userDocument.value?.display_name || user.value?.displayName || null)
  const userRole = computed(() => userDocument.value?.role ?? null)
  const isStudent = computed(() => userRole.value === 'student')
  const isProfileComplete = computed(() => {
    if (!userDocument.value) return false
    return !!userDocument.value.display_name
  })
  const photoUrl = computed(() => userDocument.value?.photo_url || user.value?.photoURL || null)

  function setUser(newUser: User | null) {
    user.value = newUser

    if (isRegistering.value) return

    if (newUser) {
      loadUserDocument(true)
    } else {
      userDocument.value = null
      // El consentimiento resuelto es por sesión: se reevalúa en el próximo ingreso.
      useLegalConsentStore().reset()
    }
  }

  async function loadUserDocument(silent = false) {
    try {
      if (!user.value?.uid) return

      const doc = await userService.getUserDocument(user.value.uid)

      if (doc) {
        userDocument.value = doc
      } else {
        if (!silent) {
          console.warn('Documento de usuario no encontrado en Firestore')
        }
        userDocument.value = null
      }
    } catch (err) {
      if (!silent) {
        console.error('Error al cargar documento de usuario:', err)
      }
      userDocument.value = null
    }
  }

  async function login(email: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const firebaseUser = await loginWithEmail(email, password)
      user.value = firebaseUser
      await loadUserDocument()
    } catch (err: unknown) {
      error.value = mapFirebaseAuthError(err, 'No se pudo iniciar sesión. Inténtalo de nuevo.')
      throw err
    } finally {
      loading.value = false
    }
  }

  // Tras entrar con Google desde `/register` con la casilla marcada: si la cuenta
  // es nueva, el mismo `users/{uid}` que crea el registro por correo (TRX-004) y
  // su aceptación (`signup_checkbox`). Si ya existía, no escribe nada: el guard
  // de consentimiento decide (ADR-SEC-013 local).
  async function finishGoogleSignUp(firebaseUser: User) {
    user.value = firebaseUser
    const created = await userService.ensureUserDocument(firebaseUser, 'student')
    if (created) {
      await useLegalConsentStore().acceptAtSignup(firebaseUser.uid)
    }
    await loadUserDocument(false)
  }

  // Tras entrar con Google desde `/login` (directo o por vinculación): NUNCA
  // aprovisiona. Si `users/{uid}` no existe, el modal «Un último paso» lo crea
  // después de que el estudiante acepte los términos (HU-AUTH-006).
  async function finishGoogleSignIn(firebaseUser: User) {
    user.value = firebaseUser
    await loadUserDocument(true)
  }

  /**
   * Ingreso o registro con Google. Devuelve `true` si entró; `false` si el correo
   * ya tenía cuenta con contraseña y quedó una vinculación pendiente (el aviso en
   * línea pide la contraseña — `completeGoogleLink`).
   */
  async function loginWithGoogle(options: GoogleSignInOptions = {}): Promise<boolean> {
    loading.value = true
    error.value = null
    isRegistering.value = true
    pendingGoogleLink.value = null
    const consent = useLegalConsentStore()
    const endFlow = options.acceptedTerms ? consent.beginFlow() : null

    try {
      const firebaseUser = await authLoginWithGoogle()
      if (options.acceptedTerms) {
        await finishGoogleSignUp(firebaseUser)
      } else {
        await finishGoogleSignIn(firebaseUser)
      }
      return true
    } catch (err: unknown) {
      const pending = getPendingGoogleLink(err)
      if (pending) {
        pendingGoogleLink.value = pending
        return false
      }
      error.value = mapFirebaseAuthError(err, 'No se pudo iniciar sesión con Google. Inténtalo de nuevo.')
      throw err
    } finally {
      endFlow?.()
      loading.value = false
      isRegistering.value = false
    }
  }

  /**
   * Vincula la credencial de Google pendiente a la cuenta de correo existente
   * (Op A.8). Sin comprobar `emailVerified` (ADR-SEC-012). Si la contraseña es
   * incorrecta la credencial se conserva para reintentar.
   */
  async function completeGoogleLink(password: string, email?: string) {
    const pending = pendingGoogleLink.value
    if (!pending) throw new Error('No hay una vinculación con Google pendiente')

    loading.value = true
    error.value = null
    isRegistering.value = true

    try {
      const targetEmail = pending.email ?? (email ?? '').trim()
      const linkedUser = await linkGoogleToPasswordAccount(targetEmail, password, pending.credential)
      pendingGoogleLink.value = null
      await finishGoogleSignIn(linkedUser)
    } catch (err: unknown) {
      error.value = mapFirebaseAuthError(err, 'No se pudo vincular tu cuenta con Google. Inténtalo de nuevo.')
      throw err
    } finally {
      loading.value = false
      isRegistering.value = false
    }
  }

  /**
   * Refleja en el estado de la sesión lo que el perfil acaba de escribir en
   * `users/{uid}` (cierra DEUDA-06, 2026-11-02): la barra y el menú leen
   * `displayName`/`photoUrl`, que dependen de `userDocument`. El `currentUser`
   * de Auth muta en sitio y no dispara reactividad: por eso se reasigna el doc.
   */
  function applyProfileChanges(
    changes: Partial<Pick<UserDocument, 'display_name' | 'student_id_number' | 'photo_url'>>
  ) {
    if (!userDocument.value) return
    userDocument.value = { ...userDocument.value, ...changes }
  }

  function cancelGoogleLink() {
    pendingGoogleLink.value = null
    error.value = null
  }

  /**
   * Registro por correo con la casilla de los términos marcada (la vista no deja
   * llegar aquí sin ella). Orden: cuenta de Auth → `users/{uid}` → aceptación
   * (`signup_checkbox`, best-effort) → correo de verificación (TRX-003).
   */
  async function register(email: string, password: string, displayNameVal: string) {
    loading.value = true
    error.value = null
    isRegistering.value = true
    const consent = useLegalConsentStore()
    const endFlow = consent.beginFlow()

    try {
      const firebaseUser = await authRegisterWithEmail(email, password, displayNameVal)
      user.value = firebaseUser
      // Mismo nombre derivado en Auth y en el documento (2026-11-02).
      await userService.createUserDocument(firebaseUser, 'student', displayNameVal)
      // Si falla, no bloquea: el guard pedirá el consentimiento al entrar.
      await consent.acceptAtSignup(firebaseUser.uid)

      // Send verification email best-effort — don't block registration if it fails
      try {
        await sendEmailVerification(firebaseUser)
      } catch (emailErr) {
        console.warn('No se pudo enviar correo de verificación (se puede reintentar):', emailErr)
      }

      await loadUserDocument(false)
    } catch (err: unknown) {
      error.value = mapFirebaseAuthError(err, 'No se pudo crear la cuenta. Inténtalo de nuevo.')
      throw err
    } finally {
      endFlow()
      loading.value = false
      isRegistering.value = false
    }
  }

  async function logout() {
    loading.value = true
    error.value = null

    try {
      await firebaseLogout()
      user.value = null
      userDocument.value = null
      pendingGoogleLink.value = null
      useLegalConsentStore().reset()
    } catch (err: unknown) {
      error.value = mapFirebaseAuthError(err, 'No se pudo cerrar la sesión. Inténtalo de nuevo.')
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    userDocument,
    loading,
    error,
    isAuthenticated,
    userId,
    userEmail,
    displayName,
    userRole,
    isStudent,
    isProfileComplete,
    photoUrl,
    hasPendingGoogleLink,
    pendingLinkEmail,
    setUser,
    login,
    loginWithGoogle,
    completeGoogleLink,
    cancelGoogleLink,
    register,
    logout,
    loadUserDocument,
    applyProfileChanges,
  }
})
